import React, { useEffect, useState } from 'react';
import { vendorProfileAPI } from '../../services/api';
import { TableSkeleton, EmptyState } from '../../components/skeletons';

/**
 * Admin KYC review queue. Tabs for each status; clicking a row opens a
 * details drawer with all the uploaded documents and approve / reject
 * actions. Rejections require a reason — the vendor sees it on their
 * dashboard and can edit + resubmit.
 */

const STATUS_TABS = [
  { v: 'submitted', label: 'Awaiting review' },
  { v: 'verified',  label: 'Verified' },
  { v: 'rejected',  label: 'Rejected' },
  { v: 'draft',     label: 'Draft' },
];

const badge = (status) => {
  const map = {
    submitted: { bg: '#FFF8E7', fg: '#8A5A00', label: 'SUBMITTED' },
    verified:  { bg: '#E8F5EE', fg: '#1B5C3A', label: 'VERIFIED' },
    rejected:  { bg: '#FEE2E2', fg: '#B91C1C', label: 'REJECTED' },
    draft:     { bg: '#F3F4F6', fg: '#6B7280', label: 'DRAFT' },
  };
  const x = map[status] || map.draft;
  return <span style={{ background: x.bg, color: x.fg, padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>{x.label}</span>;
};

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  h1: { fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, marginBottom: 4 },
  sub: { fontSize: 13, color: 'var(--muted)', marginBottom: 18 },
  tabs: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  tab: (active) => ({
    padding: '7px 16px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    border: active ? 'none' : '1.5px solid var(--border)',
    background: active ? 'var(--green)' : '#fff',
    color: active ? '#fff' : 'var(--muted)',
  }),
  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 10, cursor: 'pointer', transition: 'box-shadow 0.15s' },
  drawerOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'flex-end', zIndex: 100 },
  drawer: { width: 'min(560px, 100%)', background: '#fff', overflowY: 'auto', padding: 24 },
  field: { display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, padding: '6px 0', fontSize: 13, borderBottom: '1px dashed var(--border)' },
  fieldKey: { color: 'var(--muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 },
  docLink: { color: 'var(--green)', fontWeight: 700, textDecoration: 'none' },
  btn: (variant) => ({
    background: variant === 'danger' ? '#B91C1C' : variant === 'primary' ? 'var(--green)' : 'transparent',
    color: variant === 'outline' ? 'var(--char)' : '#fff',
    border: variant === 'outline' ? '1px solid var(--border)' : 'none',
    padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  }),
};

export default function VendorKycReview() {
  const [status, setStatus] = useState('submitted');
  const [profiles, setProfiles] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    vendorProfileAPI.adminListKyc(status)
      .then(r => setProfiles(r.data.data || []))
      .catch(() => setProfiles([]))
      .finally(() => setLoading(false));

    // Refresh counts for all tabs
    Promise.all(STATUS_TABS.map(t =>
      vendorProfileAPI.adminListKyc(t.v).then(r => [t.v, (r.data.data || []).length]).catch(() => [t.v, 0])
    )).then(pairs => setCounts(Object.fromEntries(pairs)));
  };

  useEffect(load, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async () => {
    setBusy(true);
    try { await vendorProfileAPI.adminApproveKyc(selected._id); setSelected(null); load(); }
    catch (e) { alert(e.response?.data?.message || 'Approve failed'); }
    setBusy(false);
  };
  const reject = async () => {
    if (!rejectReason.trim()) return alert('Enter a rejection reason.');
    setBusy(true);
    try { await vendorProfileAPI.adminRejectKyc(selected._id, rejectReason.trim()); setSelected(null); setRejectReason(''); load(); }
    catch (e) { alert(e.response?.data?.message || 'Reject failed'); }
    setBusy(false);
  };

  return (
    <div style={S.page}>
      <div style={S.h1}>Vendor KYC review</div>
      <div style={S.sub}>Every vendor must pass KYC before they can receive leads. Submitted applications land here.</div>

      <div style={S.tabs}>
        {STATUS_TABS.map(t => (
          <button key={t.v} style={S.tab(status === t.v)} onClick={() => setStatus(t.v)}>
            {t.label} {counts[t.v] != null && <span style={{ opacity: 0.7, marginLeft: 4 }}>({counts[t.v]})</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : profiles.length === 0 ? (
        <EmptyState title="No vendors found" description={`There are currently no vendors with status "${status}".`} />
      ) : (
        profiles.map(p => (
          <div key={p._id} style={S.card} onClick={() => setSelected(p)}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--char)', marginBottom: 4 }}>
                  {p.firmName} {badge(p.kyc?.status)}
                  {p.vendorType === 'financial' && <span style={{ marginLeft: 8, fontSize: 10, background: '#FFF4D6', color: '#8A5A00', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>🏦 FINANCIAL</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {p.userId?.fullName} · {p.userId?.email} · {p.city}, {p.state}
                  {p.kyc?.submittedAt && <> · submitted {new Date(p.kyc.submittedAt).toLocaleDateString('en-IN')}</>}
                </div>
                {p.kyc?.status === 'rejected' && p.kyc?.rejectionReason && (
                  <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 4 }}>Reason: {p.kyc.rejectionReason}</div>
                )}
              </div>
              <span style={{ fontSize: 18, color: 'var(--muted)' }}>›</span>
            </div>
          </div>
        ))
      )}

      {/* Detail drawer */}
      {selected && (
        <div style={S.drawerOverlay} onClick={() => setSelected(null)}>
          <div style={S.drawer} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700 }}>{selected.firmName}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                  {selected.userId?.fullName} · {selected.userId?.email} · {selected.userId?.mobileNumber || '—'}
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
            </div>

            <div style={{ marginBottom: 14 }}>{badge(selected.kyc?.status)}</div>

            <Section title="Business basics">
              <Field k="Vendor type" v={selected.vendorType} />
              <Field k="Firm name" v={selected.firmName} />
              <Field k="GSTIN" v={selected.gstin || '—'} />
              <Field k="City / State" v={`${selected.city}, ${selected.state}${selected.pincode ? ' · ' + selected.pincode : ''}`} />
              <Field k="Address" v={[selected.addressLine1, selected.addressLine2].filter(Boolean).join(', ') || '—'} />
            </Section>

            <Section title="Identity">
              <Field k="PAN number" v={selected.kyc?.panNumber || '—'} />
              <Field k="Aadhaar (last 4)" v={selected.kyc?.aadhaarLast4 || '—'} />
              <Field k="PAN card" v={selected.kyc?.panCardUrl ? <a style={S.docLink} href={selected.kyc.panCardUrl} target="_blank" rel="noopener noreferrer">📎 View</a> : '—'} />
              <Field k="Address proof" v={selected.kyc?.addressProofUrl ? <a style={S.docLink} href={selected.kyc.addressProofUrl} target="_blank" rel="noopener noreferrer">📎 View</a> : '—'} />
            </Section>

            <Section title="Business registration">
              <Field k="Type" v={selected.kyc?.businessType || '—'} />
              <Field k="Legal name" v={selected.kyc?.legalBusinessName || '—'} />
              <Field k="Reg. no." v={selected.kyc?.businessRegistrationNo || '—'} />
              <Field k="Business PAN" v={selected.kyc?.businessPanNumber || '—'} />
              <Field k="GST certificate" v={selected.kyc?.gstCertificateUrl ? <a style={S.docLink} href={selected.kyc.gstCertificateUrl} target="_blank" rel="noopener noreferrer">📎 View</a> : '—'} />
            </Section>

            <Section title="Bank account">
              <Field k="Account holder" v={selected.kyc?.bankAccountHolder || '—'} />
              <Field k="Bank" v={selected.kyc?.bankName || '—'} />
              <Field k="Account no." v={selected.kyc?.bankAccountNumber || '—'} />
              <Field k="IFSC" v={selected.kyc?.bankIfsc || '—'} />
              <Field k="Cancelled cheque" v={selected.kyc?.cancelledChequeUrl ? <a style={S.docLink} href={selected.kyc.cancelledChequeUrl} target="_blank" rel="noopener noreferrer">📎 View</a> : '—'} />
            </Section>

            {selected.vendorType === 'financial' && (
              <Section title="🏦 Financial institution">
                <Field k="Type" v={selected.kyc?.financialInstitutionType || '—'} />
                <Field k="RBI license no." v={selected.kyc?.rbiLicenseNumber || '—'} />
                <Field k="RBI license doc" v={selected.kyc?.rbiLicenseUrl ? <a style={S.docLink} href={selected.kyc.rbiLicenseUrl} target="_blank" rel="noopener noreferrer">📎 View</a> : '—'} />
              </Section>
            )}

            {/* Actions */}
            {selected.kyc?.status === 'submitted' && (
              <div style={{ marginTop: 20, padding: 16, background: '#fafaf7', borderRadius: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Decide</div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  <button style={S.btn('primary')} onClick={approve} disabled={busy}>{busy ? 'Working…' : '✓ Approve'}</button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>To reject, enter a reason visible to the vendor:</div>
                <textarea
                  rows={2}
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="e.g. PAN card image unreadable; bank account holder name doesn't match profile."
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', marginBottom: 10, boxSizing: 'border-box' }}
                />
                <button style={S.btn('danger')} onClick={reject} disabled={busy || !rejectReason.trim()}>
                  {busy ? 'Working…' : '✕ Reject with reason'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>{title}</div>
      <div>{children}</div>
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 10, padding: '6px 0', fontSize: 13, borderBottom: '1px dashed var(--border)' }}>
      <div style={{ color: 'var(--muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k}</div>
      <div style={{ color: 'var(--char)', wordBreak: 'break-word' }}>{v}</div>
    </div>
  );
}
